import { useEffect, useState } from 'react';
import { peopleApi } from '../services/api';
import type { CandidateSourceLayersResponse } from '../types/candidateDetails';

/**
 * Phase 3E-3 — Commit 4 (C4).
 *
 * Shared lazy loader for `/api/people/:id/source-layers`. Used by the
 * Experience / Skills / Education tabs so they can show the raw `cv`
 * layer and the `candidate_input` layer alongside the merged projection.
 *
 * Contract:
 *   - While `enabled === false` (e.g. user is on "Current"), nothing is
 *     fetched. The hook returns `null` layers.
 *   - First time `enabled` flips true, the request fires once. Subsequent
 *     flips are no-ops (cache hit).
 *   - If the personId changes, cached layers are discarded so we re-fetch
 *     for the new candidate.
 *
 * Note: `layersLoading` is intentionally NOT included in the effect's
 * dependency array — otherwise setting it would re-run the effect's
 * cleanup (flipping `cancelled=true`) before the in-flight promise
 * resolves, stranding the component on a permanent loading spinner. This
 * replicates the bugfix originally applied inline to ExperienceTab.
 */
export function useCandidateSourceLayers(
  personId: number | undefined,
  enabled: boolean
): {
  layers: CandidateSourceLayersResponse | null;
  loading: boolean;
  error: string | null;
} {
  const [layers, setLayers] = useState<CandidateSourceLayersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLayers(null);
    setError(null);
    setLoading(false);
  }, [personId]);

  useEffect(() => {
    if (!enabled) return;
    if (layers) return;
    if (!personId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    peopleApi
      .getSourceLayers(personId)
      .then((res) => {
        if (!cancelled) setLayers(res);
      })
      .catch((e: any) => {
        if (!cancelled) {
          const msg =
            e?.response?.data?.error ||
            e?.message ||
            'Failed to load source layers';
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally omit `loading` from deps (see JSDoc above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, layers, personId]);

  return { layers, loading, error };
}
